/**
 * The totals above the decisions list (DEC-28 C1).
 *
 * Every figure here follows the list's own filter, so "how many" and "which
 * ones" are always the same question. An outcome tile filters the list when
 * pressed; a refusal reason with one word of the authority's own filters it
 * on that word.
 */

import {
  Button,
  ToggleButton,
  makeStyles,
  tokens,
} from '@fluentui/react-components'
import { EmptyState, KpiCard, SectionCard, StatusBadge } from '../platform'
import type { DecisionTotals as Totals } from '../api'
import { TimeChart } from './Charts'
import { OUTCOME_ORDER, outcomeColour, useChartPalette } from './chartPalette'
import { count, deciderRows, milliseconds, shareOf, whyRows } from './totalsReading'

const useStyles = makeStyles({
  root: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM },
  tiles: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
    gap: tokens.spacingHorizontalM,
  },
  tile: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: tokens.spacingVerticalXXS,
    padding: tokens.spacingVerticalM,
    minHeight: '96px',
    height: 'auto',
    textAlign: 'left',
  },
  tileValue: { fontSize: tokens.fontSizeHero700, fontWeight: tokens.fontWeightBold },
  tileSub: { color: tokens.colorNeutralForeground3, fontWeight: tokens.fontWeightRegular },
  charts: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 420px), 1fr))',
    gap: tokens.spacingHorizontalM,
  },
  bars: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS },
  barLine: { display: 'flex', justifyContent: 'space-between', gap: tokens.spacingHorizontalS },
  barTrack: {
    height: '8px',
    borderRadius: tokens.borderRadiusCircular,
    backgroundColor: tokens.colorNeutralBackground3,
    overflow: 'hidden',
  },
  barFill: {
    height: '8px',
    borderRadius: tokens.borderRadiusCircular,
    backgroundColor: tokens.colorBrandBackground,
  },
  why: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 320px), 1fr))',
    gap: tokens.spacingHorizontalS,
  },
  whyButton: { justifyContent: 'space-between', width: '100%', height: 'auto', textAlign: 'left' },
  whyStatic: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalS,
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalM}`,
  },
  whyLabel: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS },
  scope: { color: tokens.colorNeutralForeground2 },
})

const OUTCOME_STATUS = { APPROVED: 'success', BLOCKED: 'error', UNKNOWN: 'warning' } as const

export function DecisionTotals({
  totals,
  scope,
  outcome,
  onOutcome,
  onReason,
}: {
  totals: Totals
  scope: string
  outcome: string | null
  onOutcome: (outcome: string | null) => void
  onReason: (reason: string) => void
}) {
  const styles = useStyles()
  const palette = useChartPalette()
  const deciders = deciderRows(totals.by_decided_by)
  const widest = Math.max(1, ...deciders.map((d) => d.count))
  const why = whyRows(totals.why_not)
  const second = totals.second_ask_share

  const tile = (key: string | null, label: string, value: number, sub: string) => (
    <ToggleButton
      key={label}
      className={styles.tile}
      appearance="outline"
      aria-label={`${label}, ${count(value)}, ${sub}`}
      checked={outcome === key}
      onClick={() => onOutcome(outcome === key ? null : key)}
    >
      <span>{label}</span>
      <span className={styles.tileValue}>{count(value)}</span>
      <span className={styles.tileSub}>{sub}</span>
    </ToggleButton>
  )

  return (
    <section aria-label="Totals" className={styles.root}>
      <span className={styles.scope}>Totals for {scope}</span>
      <div className={styles.tiles}>
        {tile(
          null, 'Asks', totals.total,
          second === null ? 'nothing asked' : `${Math.round(second * 100)}% repeat an ask within 2 minutes`,
        )}
        {tile('APPROVED', 'Approved', totals.by_outcome.APPROVED, shareOf(totals.by_outcome.APPROVED, totals.total))}
        {tile('BLOCKED', 'Blocked', totals.by_outcome.BLOCKED, shareOf(totals.by_outcome.BLOCKED, totals.total))}
        {tile('UNKNOWN', 'Unknown', totals.by_outcome.UNKNOWN, shareOf(totals.by_outcome.UNKNOWN, totals.total))}
        <KpiCard
          title="Distinct"
          value={count(totals.distinct_tokens)}
          caption={`tokens at ${count(totals.distinct_charge_points)} charge points`}
        />
        <KpiCard
          title="Answer time"
          value={milliseconds(totals.latency.p50)}
          caption={`median, 95% within ${milliseconds(totals.latency.p95)}`}
        />
      </div>

      <div className={styles.charts}>
        <SectionCard title={totals.bucket === 'hour' ? 'Asks per hour' : 'Asks per day'}>
          <TimeChart
            title={totals.bucket === 'hour' ? 'Asks per hour' : 'Asks per day'}
            buckets={totals.buckets.map((b) => b.t)}
            bucketSeconds={totals.bucket === 'hour' ? 3600 : 86400}
            zone="Europe/Berlin"
            bars={OUTCOME_ORDER.map((name) => ({
              key: name,
              name: name.charAt(0) + name.slice(1).toLowerCase(),
              colour: outcomeColour(palette, name),
              values: totals.buckets.map((b) => b[name.toLowerCase() as 'approved' | 'blocked' | 'unknown']),
            }))}
          />
        </SectionCard>
        <SectionCard title="Who decided">
          {deciders.length === 0 ? (
            <EmptyState title="Nothing decided" description="No decision matches this filter." />
          ) : (
            <div className={styles.bars}>
              {deciders.map((d) => (
                <div key={d.label}>
                  <div className={styles.barLine}>
                    <span>{d.label}</span>
                    <span>{count(d.count)}</span>
                  </div>
                  <div className={styles.barTrack} aria-hidden="true">
                    <div className={styles.barFill} style={{ width: `${Math.max(2, (d.count * 100) / widest)}%` }} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </SectionCard>
      </div>

      {why.length > 0 ? (
        <SectionCard title="Why it was not approved">
          <div className={styles.why}>
            {why.map((row) => {
              const line = (
                <>
                  <span className={styles.whyLabel}>
                    <StatusBadge
                      status={OUTCOME_STATUS[row.outcome as keyof typeof OUTCOME_STATUS] ?? 'info'}
                      label={row.outcome}
                    />
                    {row.label}
                  </span>
                  <span>{count(row.count)}</span>
                </>
              )
              // A row whose decisions share one word of the authority's own
              // filters the list on it; a row worded from several cannot.
              return row.reason ? (
                <Button
                  key={`${row.outcome}|${row.label}`}
                  className={styles.whyButton}
                  appearance="subtle"
                  onClick={() => onReason(row.reason as string)}
                >
                  {line}
                </Button>
              ) : (
                <div key={`${row.outcome}|${row.label}`} className={styles.whyStatic}>
                  {line}
                </div>
              )
            })}
          </div>
        </SectionCard>
      ) : null}
    </section>
  )
}
